import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useCallback } from "react";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  collisionDetectionAlgorithm,
  SortableItem,
} from "./SortableObjectList";

export default function SortableList<Item extends { id: string }>({
  list,
  setList,
  renderItemComponent,
  disabled,
}: {
  list: Item[];
  setList: (newList: Item[]) => void;
  renderItemComponent: React.FC<{
    id: string;
    index: number;
    dragHandle: React.ReactNode;
    firstIndex: number;
  }>;
  disabled?: boolean;
}): React.ReactElement | null {
  const sensors = useSensors(useSensor(PointerSensor));
  const isDisabled = disabled || list.length < 2;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      document.body.style.cursor = "default";

      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = list.findIndex(({ id }) => id === active.id);
        const newIndex = list.findIndex(({ id }) => id === over.id);

        setList(arrayMove(list, oldIndex, newIndex));
      }
    },
    [setList, list]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionAlgorithm}
      onDragStart={() => {
        document.body.style.cursor = "grabbing";
      }}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={list}
        strategy={verticalListSortingStrategy}
        disabled={isDisabled}
      >
        {list.map(({ id }, index) => (
          <SortableItem
            key={id}
            id={id}
            index={index}
            renderChildren={renderItemComponent}
            disabled={isDisabled}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}
